/**
 * ZipCodeForm: formulaire Pays + Code postal (+ province ES/IT) → prepare_shipping_rates + async_shipping_rates.
 * Affiche les tarifs de livraison et expose data-has-rates pour l'activation du checkout.
 * Persistance sessionStorage + ré-estimation automatique après refresh du panier (même onglet).
 */
if (!customElements.get("zip-code-form")) {
  const PREPARE_DELAY_MS = 1000;
  const ASYNC_RETRY_DELAY_MS = 800;
  const ASYNC_MAX_RETRIES = 15;
  const ZIP_PERSIST_DEBOUNCE_MS = 300;

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  /** Valeurs `option[value]` de country_option_tags (noms anglais + variantes vitrine). */
  const COUNTRIES_NEEDING_PROVINCE = ["Spain", "Italy", "España", "Italia", "Espagne", "Italie"];

  class ZipCodeForm extends HTMLElement {
    constructor() {
      super();
      this.hasRates = false;
      this._abortController = null;
      this._persistZipTimeout = null;
    }

    _storageKey() {
      const ctx = (this.dataset.context || "drawer").trim();
      return `twood:cart-shipping:${ctx}`;
    }

    _saveState() {
      if (!this.countrySelect || !this.zipInput) return;
      try {
        const payload = {
          country: (this.countrySelect.value || "").trim(),
          zip: (this.zipInput.value || "").trim(),
          province: this._provinceRowVisible() ? (this.provinceSelect?.value || "").trim() : "",
          hadSuccessfulEstimate: this.hasRates === true,
        };
        sessionStorage.setItem(this._storageKey(), JSON.stringify(payload));
      } catch (_) {}
    }

    _readStoredState() {
      try {
        const raw = sessionStorage.getItem(this._storageKey());
        if (!raw) return null;
        const data = JSON.parse(raw);
        if (!data || typeof data !== "object") return null;
        return data;
      } catch (_) {
        return null;
      }
    }

    /**
     * Réapplique pays / CP / province depuis sessionStorage (après innerHTML du panier).
     * Si une estimation avait réussi, relance la même chaîne que le bouton Estimer.
     */
    async _applyPersistedState() {
      if (!this.countrySelect || !this.zipInput) return;

      const data = this._readStoredState();
      if (!data) return;

      const country = (data.country || "").trim();
      if (country && country !== "---") {
        let found = false;
        for (let i = 0; i < this.countrySelect.options.length; i++) {
          if (this.countrySelect.options[i].value === country) {
            this.countrySelect.selectedIndex = i;
            found = true;
            break;
          }
        }
        if (!found) return;
      }

      if (typeof data.zip === "string") {
        this.zipInput.value = data.zip;
      }

      this._syncProvinceRow();

      const prov = (data.province || "").trim();
      if (prov && this.provinceSelect && this._provinceRowVisible()) {
        for (let i = 0; i < this.provinceSelect.options.length; i++) {
          if (this.provinceSelect.options[i].value === prov) {
            this.provinceSelect.selectedIndex = i;
            break;
          }
        }
      }

      this._updateSubmitButton();

      if (data.hadSuccessfulEstimate && this._canSubmit()) {
        await this._onSubmit();
      }
    }

    connectedCallback() {
      this.countrySelect = this.querySelector(".shipping-country");
      this.zipInput = this.querySelector(".shipping-zip");
      this.provinceRow = this.querySelector("[data-province-row]");
      this.provinceSelect = this.querySelector(".shipping-province");
      this.submitBtn = this.querySelector(".zip-code-form__submit");
      this.submitTextEl = this.querySelector(".zip-code-form__submit-text");
      this.loadingEl = this.querySelector(".zip-code-form__loading");
      this.resultsEl = this.closest(".cart__zip-code-form")?.querySelector(".zip-code-form__label");
      this.errorMessage = this.querySelector(".error-message");
      this.toggleBtn = this.closest(".cart__zip-code-form")?.querySelector(".zip-code-form__toggle");

      if (!this.countrySelect || !this.zipInput || !this.submitBtn) return;

      this._setDefaultCountry();
      this._syncProvinceRow();
      this._updateSubmitButton();
      this.submitBtn.addEventListener("click", this._onSubmit.bind(this));
      this.countrySelect.addEventListener("change", () => {
        this._syncProvinceRow();
        this._updateSubmitButton();
        this._saveState();
      });
      this.zipInput.addEventListener("input", () => {
        this._updateSubmitButton();
        if (this._persistZipTimeout) clearTimeout(this._persistZipTimeout);
        this._persistZipTimeout = setTimeout(() => {
          this._persistZipTimeout = null;
          this._saveState();
        }, ZIP_PERSIST_DEBOUNCE_MS);
      });
      this.zipInput.addEventListener("change", () => {
        if (this._persistZipTimeout) {
          clearTimeout(this._persistZipTimeout);
          this._persistZipTimeout = null;
        }
        this._saveState();
      });
      if (this.provinceSelect) {
        this.provinceSelect.addEventListener("change", () => {
          this._updateSubmitButton();
          this._saveState();
        });
      }

      if (this.toggleBtn) {
        this.toggleBtn.addEventListener("click", this._onToggleForm.bind(this));
      }

      queueMicrotask(() => {
        this._applyPersistedState().catch(() => {});
      });
    }

    disconnectedCallback() {
      if (this._persistZipTimeout) {
        clearTimeout(this._persistZipTimeout);
        this._persistZipTimeout = null;
      }
    }

    _onToggleForm() {
      this.style.display = "";
      if (this.toggleBtn) this.toggleBtn.style.display = "none";
    }

    _setFormVisible(visible) {
      this.style.display = visible ? "" : "none";
      if (this.toggleBtn) this.toggleBtn.style.display = visible ? "none" : "inline";
    }

    _needsProvinceCountry(country) {
      const c = (country || "").trim();
      return COUNTRIES_NEEDING_PROVINCE.includes(c);
    }

    _provinceRowVisible() {
      return !!(this.provinceRow && !this.provinceRow.hidden && this.provinceSelect);
    }

    _canSubmit() {
      const zip = (this.zipInput?.value || "").trim();
      const country = (this.countrySelect?.value || "").trim();
      if (!(zip.length > 0 && country.length > 0 && country !== "---")) return false;
      if (this._provinceRowVisible() && !(this.provinceSelect?.value || "").trim()) return false;
      return true;
    }

    _updateSubmitButton() {
      if (!this.submitBtn || this.submitBtn.classList.contains("loading")) return;
      this.submitBtn.disabled = !this._canSubmit();
    }

    _setDefaultCountry() {
      const def = this.countrySelect.getAttribute("data-default");
      if (!def) return;
      for (let i = 0; i < this.countrySelect.options.length; i++) {
        if (this.countrySelect.options[i].value === def) {
          this.countrySelect.selectedIndex = i;
          return;
        }
      }
    }

    _parseProvincesFromOption(opt) {
      if (!opt) return [];
      const raw = opt.getAttribute("data-provinces");
      try {
        const parsed = JSON.parse(raw || "[]");
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }

    _placeholderText() {
      return this.dataset.provincePlaceholder || "—";
    }

    _resetProvinceSelectEmpty() {
      if (!this.provinceSelect) return;
      this.provinceSelect.innerHTML = "";
      const ph = document.createElement("option");
      ph.value = "";
      ph.textContent = this._placeholderText();
      ph.setAttribute("disabled", "disabled");
      ph.setAttribute("selected", "selected");
      this.provinceSelect.appendChild(ph);
    }

    _syncProvinceRow() {
      if (!this.provinceRow || !this.provinceSelect || !this.countrySelect) return;

      const country = (this.countrySelect.value || "").trim();
      const opt = this.countrySelect.options[this.countrySelect.selectedIndex];

      if (!this._needsProvinceCountry(country)) {
        this.provinceRow.hidden = true;
        this._resetProvinceSelectEmpty();
        return;
      }

      const provinces = this._parseProvincesFromOption(opt);
      if (provinces.length === 0) {
        this.provinceRow.hidden = true;
        this._resetProvinceSelectEmpty();
        return;
      }

      this.provinceSelect.innerHTML = "";
      const ph = document.createElement("option");
      ph.value = "";
      ph.textContent = this._placeholderText();
      ph.setAttribute("disabled", "disabled");
      ph.setAttribute("selected", "selected");
      this.provinceSelect.appendChild(ph);

      provinces.forEach((pair) => {
        const value = Array.isArray(pair) ? pair[0] : pair;
        const label = Array.isArray(pair) ? pair[1] || pair[0] : pair;
        if (value == null || value === "") return;
        const o = document.createElement("option");
        o.value = String(value);
        o.textContent = String(label != null ? label : value);
        this.provinceSelect.appendChild(o);
      });

      this.provinceRow.hidden = false;
    }

    _getCartUrl() {
      return typeof window.theme !== "undefined" && window.theme.routes && window.theme.routes.cart_url ? window.theme.routes.cart_url : "/cart";
    }

    _getMoneyFormat() {
      const s = typeof window.theme !== "undefined" && window.theme.settings ? window.theme.settings : null;
      if (!s) return "{{amount}}";
      if (s.currency_code_enabled) {
        return s.money_with_currency_format_actual || s.money_with_currency_format || s.money_format;
      }
      return s.money_format || s.money_with_currency_format;
    }

    /**
     * Même règle que le panier (theme-shopify-ht-flag + theme_show_shopify_ht_only).
     * On lit data-shopify-ht-only sur le custom element : il est rendu par Liquid à chaque
     * re-fetch du drawer / section, alors que theme.shopify_ht_only reste figé au 1er chargement du layout.
     */
    _shopifyHtOnlyFromContext() {
      const raw = (this.dataset.shopifyHtOnly || "").trim().toLowerCase();
      if (raw === "true") return true;
      if (raw === "false") return false;
      return typeof window.theme !== "undefined" && !!window.theme.shopify_ht_only;
    }

    _formatShippingPriceLine(priceCents) {
      const format = this._getMoneyFormat() || "{{amount}}";
      const formattedTtc =
        typeof formatMoney === "function" ? formatMoney(priceCents, format) : String(priceCents / 100);
      const htCents = Math.round(priceCents / 1.2);
      const formattedHt =
        typeof formatMoney === "function" ? formatMoney(htCents, format) : String(htCents / 100);

      const t = typeof window.theme !== "undefined" ? window.theme : null;
      const str = t && t.strings ? t.strings : {};
      const labelHt = escapeHtml((str.price_without_taxes || "").trim());
      const labelTtc = escapeHtml((str.price_with_taxes || "").trim());

      if (this._shopifyHtOnlyFromContext()) {
        return `${escapeHtml(formattedTtc)}${labelHt ? ` ${labelHt}` : ""}`;
      }

      return `<span class="zip-code-form__estimate-prices"><span class="with-taxes">${escapeHtml(formattedTtc)}${labelTtc ? ` ${labelTtc}` : ""}</span><span class="without-taxes text-sm">${escapeHtml(formattedHt)}${labelHt ? ` ${labelHt}` : ""}</span></span>`;
    }

    _params() {
      const zip = (this.zipInput.value || "").trim();
      const country = (this.countrySelect.value || "").trim();
      const province = this._provinceRowVisible() ? (this.provinceSelect?.value || "").trim() : "";
      return {
        "shipping_address[zip]": zip,
        "shipping_address[country]": country,
        "shipping_address[province]": province,
      };
    }

    _setState(hasRates) {
      this.hasRates = hasRates;
      this.setAttribute("data-has-rates", hasRates ? "true" : "false");
      this.dispatchEvent(new CustomEvent("zip-code-form-update", { bubbles: true, detail: { hasRates } }));
    }

    _setLoading(loading) {
      if (this.loadingEl) this.loadingEl.style.display = loading ? "inline-flex" : "none";
      if (this.submitTextEl) this.submitTextEl.style.display = loading ? "none" : "";
      if (this.submitBtn) {
        this.submitBtn.disabled = loading ? true : !this._canSubmit();
        this.submitBtn.classList.toggle("loading", loading);
      }
    }

    _showError(show) {
      if (this.errorMessage) this.errorMessage.style.display = show ? "flex" : "none";
      if (this.zipInput) this.zipInput.classList.toggle("error", show);
      if (this.provinceSelect) this.provinceSelect.classList.toggle("error", show);
    }

    _showResults(html) {
      if (!this.resultsEl) return;
      const defaultLabel = this.resultsEl.getAttribute("data-default-label") || "";
      if (html !== "" && html.indexOf("<") === -1) {
        this.resultsEl.textContent = html;
      } else {
        this.resultsEl.innerHTML = html !== "" ? html : defaultLabel;
      }
    }

    async _onSubmit() {
      const zip = (this.zipInput.value || "").trim();
      const country = (this.countrySelect.value || "").trim();

      if (!zip || !country || country === "---" || !this._canSubmit()) {
        this._showError(true);
        this._showResults("");
        this._setState(false);
        this._saveState();
        return;
      }

      this._showError(false);
      this._showResults("");
      this._setState(false);
      this._setLoading(true);

      try {
        await this._runEstimation();
      } finally {
        this._setLoading(false);
      }
    }

    async _runEstimation() {
      if (this._abortController) this._abortController.abort();
      this._abortController = new AbortController();
      const signal = this._abortController.signal;

      const params = this._params();

      try {
        const queryString = new URLSearchParams(params).toString();
        const baseUrl = this._getCartUrl();

        const prepareRes = await fetch(`${baseUrl}/prepare_shipping_rates.json?${queryString}`, {
          method: "POST",
          signal,
        });

        if (!prepareRes.ok) {
          this._showError(true);
          this._showResults(this.resultsEl?.getAttribute("data-default-label") || "");
          this._setState(false);
          this._saveState();
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, PREPARE_DELAY_MS));
        if (signal.aborted) return;

        const rates = await this._fetchAsyncRates(queryString, baseUrl, signal);
        if (signal.aborted) return;

        if (rates && Array.isArray(rates) && rates.length > 0) {
          const r = rates[0];
          const priceCents = Math.round(parseFloat(r.price || 0) * 100);
          const line = this._formatShippingPriceLine(priceCents);
          if (this.resultsEl) this.resultsEl.classList.remove("text-sm");
          this._showResults(line);
          this._setState(true);
          this._setFormVisible(false);
          this._saveState();
        } else {
          this._showError(true);
          this._showResults(this.resultsEl?.getAttribute("data-default-label") || "");
          this._setState(false);
          this._saveState();
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        this._showError(true);
        this._showResults(this.resultsEl?.getAttribute("data-default-label") || "");
        this._setState(false);
        this._saveState();
      }
    }

    async _fetchAsyncRates(queryString, baseUrl, signal, attempt = 0) {
      const res = await fetch(`${baseUrl}/async_shipping_rates.json?${queryString}`, { signal });
      const text = await res.text();

      if (!res.ok) {
        throw new Error("Erreur async_shipping_rates: HTTP " + res.status);
      }

      if (text === "null" || text.trim() === "") {
        if (attempt >= ASYNC_MAX_RETRIES) {
          return [];
        }
        await new Promise((r) => setTimeout(r, ASYNC_RETRY_DELAY_MS));
        if (signal.aborted) return null;
        return this._fetchAsyncRates(queryString, baseUrl, signal, attempt + 1);
      }

      let data;
      try {
        data = JSON.parse(text);
      } catch (_) {
        return [];
      }

      return data && data.shipping_rates ? data.shipping_rates : [];
    }
  }

  customElements.define("zip-code-form", ZipCodeForm);
}
